import React, { useState, useEffect } from 'react';
import { ArrowLeft, Package, Phone, Mail, MapPin, Calendar, CreditCard, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    document.title = 'Admin - Orders | RSD Accessories';
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => 
    order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer_phone?.includes(searchTerm) ||
    order.razorpay_order_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'created': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 hover:bg-gray-200 rounded-full">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Orders Dashboard</h1>
              <p className="text-gray-600">Manage and view all customer orders</p>
            </div>
          </div>
          <Button onClick={fetchOrders} variant="outline" className="flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by name, phone, or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 py-6 text-lg"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <p className="text-gray-600 text-sm">Total Orders</p>
            <p className="text-3xl font-bold text-gray-900">{orders.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <p className="text-gray-600 text-sm">Paid Orders</p>
            <p className="text-3xl font-bold text-green-600">
              {orders.filter(o => o.status === 'paid').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <p className="text-gray-600 text-sm">Pending Orders</p>
            <p className="text-3xl font-bold text-yellow-600">
              {orders.filter(o => o.status === 'created').length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <p className="text-gray-600 text-sm">Total Revenue</p>
            <p className="text-3xl font-bold text-[#FF4D00]">
              ₹{orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + (o.amount || 0), 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF4D00]"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600">No orders found</h3>
            <p className="text-gray-500">Orders will appear here when customers place them</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <div key={order.id || order.razorpay_order_id} className="bg-white rounded-xl shadow-sm border overflow-hidden">
                {/* Order Header */}
                <div className="bg-gray-50 px-6 py-4 border-b flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      {order.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                    <span className="font-mono text-sm text-gray-600">{order.razorpay_order_id}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <Calendar className="w-4 h-4" />
                    {formatDate(order.created_at)}
                  </div>
                </div>

                {/* Order Content */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Customer Details */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-8 h-8 bg-[#FF4D00] text-white rounded-full flex items-center justify-center text-sm">
                          {order.customer_name?.charAt(0) || '?'}
                        </span>
                        Customer Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p className="font-semibold text-lg">{order.customer_name || 'N/A'}</p>
                        <p className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-[#FF4D00]" />
                          <a href={`tel:${order.customer_phone}`} className="hover:text-[#FF4D00]">
                            {order.customer_phone || 'N/A'}
                          </a>
                        </p>
                        {order.customer_email && (
                          <p className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4 text-[#FF4D00]" />
                            <a href={`mailto:${order.customer_email}`} className="hover:text-[#FF4D00]">
                              {order.customer_email}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#FF4D00]" />
                        Shipping Address
                      </h4>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {order.customer_address || 'Address not provided'}
                      </p>
                    </div>

                    {/* Order Amount */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-[#FF4D00]" />
                        Payment Details
                      </h4>
                      <div className="space-y-2">
                        <p className="text-3xl font-bold text-[#FF4D00]">₹{order.amount?.toLocaleString() || 0}</p>
                        {order.promo_code && (
                          <p className="text-sm text-green-600">
                            Promo: {order.promo_code} (-₹{order.promo_discount})
                          </p>
                        )}
                        {order.valentine_discount > 0 && (
                          <p className="text-sm text-pink-600">
                            💕 Valentine Discount: -₹{order.valentine_discount}
                          </p>
                        )}
                        {order.payment_id && (
                          <p className="text-xs text-gray-500 font-mono">
                            Payment ID: {order.payment_id}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  {order.items && order.items.length > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <h4 className="font-semibold text-gray-900 mb-3">Order Items ({order.items.length})</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded-lg flex items-center gap-3">
                            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{item.name || item.product_id}</p>
                              <p className="text-xs text-gray-500">Qty: {item.quantity} × ₹{item.price}</p>
                              {item.customization?.engraving && (
                                <p className="text-xs text-amber-600">✨ Engraved: "{item.customization.engraving}"</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
